/**
 * GOOGLE OAUTH CALLBACK — /google-ads/youtube/callback
 *
 * The backend completes the code exchange itself and redirects the tab back
 * with either `?connected=1&email=…` or `?error=…`. This page turns that into
 * a short confirmation and returns to the workspace.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Spinner, YT_BASE } from '@/components/google-ads/youtube/youtubeShared';

export default function YouTubeCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [state, setState] = useState<{ s: 'working' } | { s: 'done' } | { s: 'error'; message: string }>({ s: 'working' });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const gError = params.get('error');
    const connected = params.get('connected');

    if (gError) {
      setState({ s: 'error', message: `Google reported: ${decodeURIComponent(gError)}. Start the connection again.` });
      return;
    }
    if (!connected) {
      setState({ s: 'error', message: 'Google did not confirm the connection. Start the connection again.' });
      return;
    }

    setState({ s: 'done' });
    const t = setTimeout(() => navigate(YT_BASE, { replace: true }), 900);
    return () => clearTimeout(t);
  }, [params, navigate]);

  return (
    <>
      <Topbar title="YouTube Ads" breadcrumb="Google Ads / YouTube" description="Finishing the Google connection" autoBack={false} />
      <div className="w-full px-7 py-7">
        <Card className="mx-auto mt-10 w-full max-w-md p-8 text-center">
          {state.s === 'working' && (
            <>
              <Spinner size={32} className="mx-auto block text-primary" />
              <p className="mt-4 text-sm text-text-muted">Completing the Google sign-in…</p>
            </>
          )}
          {state.s === 'done' && (
            <>
              <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
              <p className="mt-4 text-sm text-text-primary">Google connected. Taking you back…</p>
            </>
          )}
          {state.s === 'error' && (
            <>
              <XCircle className="mx-auto h-8 w-8 text-danger" />
              <p className="mt-4 break-words text-sm text-text-muted">{state.message}</p>
              <Button className="mt-5" onClick={() => navigate(YT_BASE, { replace: true })}>
                Back to YouTube Ads
              </Button>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
